import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraSpinner } from '@mono/ui-components/ensigns';
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useFeatureFlag, useFeatureFlagsReady } from '../../contexts/featureFlagContext';
import { safeReturnTo } from '../Delivery/Registries/returnTo';
/** How long a record route waits for Flipt before treating the flag as off. */ export const FLAGS_READY_TIMEOUT_MS = 3000;
/** Keeps a valid `returnTo` when a record URL falls back to the drawer. */ function withReturnTo(target, search) {
    const returnTo = safeReturnTo(new URLSearchParams(search).get('returnTo'));
    if (!returnTo) return target;
    const joiner = target.includes('?') ? '&' : '?';
    return `${target}${joiner}returnTo=${encodeURIComponent(returnTo)}`;
}
/**
 * Gate for the registry record routes (REG-08). Flag off ⇒ redirect to the
 * equivalent drawer URL, so old and new links land on the same record and
 * nothing user-visible changes. While flags load, a spinner shows for at most
 * FLAGS_READY_TIMEOUT_MS; after that a not-ready provider counts as off (the
 * Flipt provider degrades silently when Flipt is unreachable and would
 * otherwise spin forever).
 */ export function RegistryFlagRoute({ flag, alsoRequires, fallback, children }) {
    const ready = useFeatureFlagsReady();
    const primaryOn = useFeatureFlag(flag);
    const secondaryOn = useFeatureFlag(alsoRequires ?? flag);
    const params = useParams();
    const location = useLocation();
    const [timedOut, setTimedOut] = useState(false);
    useEffect(()=>{
        if (ready) return;
        const timer = setTimeout(()=>setTimedOut(true), FLAGS_READY_TIMEOUT_MS);
        return ()=>clearTimeout(timer);
    }, [
        ready
    ]);
    if (!ready && !timedOut) {
        return /*#__PURE__*/ _jsx(SpecraSpinner, {
            label: "Loading"
        });
    }
    if (!ready || primaryOn !== true || secondaryOn !== true) {
        return /*#__PURE__*/ _jsx(Navigate, {
            to: withReturnTo(fallback(params), location.search),
            replace: true
        });
    }
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
}
